/* Native product-page links with bounded, same-origin catalog return context. */
(() => {
  'use strict';
  if (window.PVProductNavigation) { window.PVProductNavigation.init(); return; }
  const MAX_RETURN_LENGTH = 2048;
  const MAX_QUERY_LENGTH = 512;
  const PRODUCT_ID = /^[a-z0-9][a-z0-9-]{0,95}$/;
  const CATALOG_VIEWS = new Set(['pv-products', 'pv-ocean', 'pv-fairytale', 'pv-circus', 'pv-everyday']);
  const CATEGORIES = new Set(['all', 'play', 'walk', 'climb', 'rest']);
  const WORLDS = new Set(['all', 'ocean', 'fairytale', 'circus', 'everyday']);
  const LOCALE = '(?:[a-z]{2,3}(?:-[a-z0-9]{2,8}){0,2}/)?';
  const CATALOG_PATH = new RegExp(`^/${LOCALE}collections/all/?$`, 'i');
  const HOME_PATH = new RegExp('^/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8}){0,2}/?)?$', 'i');
  const PREVIEW_PARAMS = {
    preview_theme_id: /^\d{1,24}$/,
    preview_key: /^[a-zA-Z0-9._-]{1,256}$/,
    _fd: /^[01]$/,
    _sc: /^[01]$/,
    pb: /^[01]$/
  };
  let restorePending = false, restoreVersion = 0, legacyDestination = '';
  const relative = url => `${url.pathname}${url.search}${url.hash}`;
  const frame = callback => window.requestAnimationFrame ? window.requestAnimationFrame(callback) : window.setTimeout(callback, 0);
  // Inspect before URL normalization so /x/../ and encoded path separators cannot become approved routes.
  function localURL(raw, maxLength = MAX_RETURN_LENGTH) {
    if (typeof raw !== 'string' || !raw || raw.length > maxLength || /[\u0000-\u0020\u007f\\]/.test(raw)) return null;
    if (raw.startsWith('//') || (!raw.startsWith('/') && !/^https?:\/\//i.test(raw))) return null;
    const path = raw.split(/[?#]/, 1)[0];
    if (/%(?:2e|2f|5c|25)/i.test(path) || /(?:^|\/)\.{1,2}(?:\/|$)/.test(path)) return null;
    let url; try { url = new URL(raw, location.origin); } catch (_) { return null; }
    if (!/^https?:$/.test(url.protocol) || url.origin !== location.origin || url.username || url.password) return null;
    return url;
  }
  function copyPreview(source, target) {
    Object.entries(PREVIEW_PARAMS).forEach(([key, pattern]) => {
      const values = source.searchParams.getAll(key);
      if (values.length === 1 && pattern.test(values[0])) target.searchParams.set(key, values[0]);
    });
    // Editor iframe URLs can omit this ID. Keep genuine draft links on the current draft when opened outside the frame.
    const theme = window.Shopify && window.Shopify.theme;
    if (!target.searchParams.has('preview_theme_id') && theme && ['unpublished', 'development'].includes(theme.role) && PREVIEW_PARAMS.preview_theme_id.test(String(theme.id || ''))) {
      target.searchParams.set('preview_theme_id', String(theme.id));
    }
  }
  function safeReturn(raw, id) {
    if (!PRODUCT_ID.test(id || '')) return null;
    const input = localURL(raw);
    if (!input) return null;
    const isHome = HOME_PATH.test(input.pathname);
    if (!isHome && !CATALOG_PATH.test(input.pathname)) return null;
    const views = input.searchParams.getAll('view');
    if (views.length > 1 || (isHome ? views.length > 0 : views.length === 1 && !CATALOG_VIEWS.has(views[0]))) return null;
    let hash; try { hash = decodeURIComponent(input.hash); } catch (_) { return null; }
    if (hash && !/^#pv-card-[a-z0-9][a-z0-9-]{0,95}$/.test(hash)) return null;
    const output = new URL(input.pathname, location.origin);
    if (!isHome) {
      output.searchParams.set('view', views[0] || 'pv-products');
      for (const key of ['pvq', 'pvtype', 'pvworld', 'pvshow']) {
        const values = input.searchParams.getAll(key);
        if (values.length > 1) return null;
        if (!values.length) continue;
        const value = values[0];
        if (key === 'pvq' && value.length <= MAX_QUERY_LENGTH && !/[\u0000-\u001f\u007f]/.test(value)) output.searchParams.set(key, value);
        else if (key === 'pvtype' && CATEGORIES.has(value)) output.searchParams.set(key, value);
        else if (key === 'pvworld' && WORLDS.has(value)) output.searchParams.set(key, value);
        else if (key === 'pvshow' && /^\d{1,5}$/.test(value) && Number(value) > 0 && Number(value) <= 10000) output.searchParams.set(key, value);
        else return null;
      }
    }
    copyPreview(input, output);
    // Related-product navigation retains the originating card, which may be a homepage feature.
    output.hash = hash || `pv-card-${id}`;
    return relative(output).length <= MAX_RETURN_LENGTH ? output : null;
  }
  function fallbackCatalog(root) {
    const supplied = localURL(root && root.dataset.catalogUrl || '');
    const currentPath = location.pathname;
    const suppliedHome = localURL(root && root.dataset.homeUrl || '');
    const homePath = suppliedHome && HOME_PATH.test(suppliedHome.pathname) ? suppliedHome.pathname : HOME_PATH.test(currentPath) ? currentPath : '/';
    const path = supplied && CATALOG_PATH.test(supplied.pathname) ? supplied.pathname : CATALOG_PATH.test(currentPath) ? currentPath : `${homePath.replace(/\/?$/, '/')}collections/all`;
    const fallback = new URL(path, location.origin);
    fallback.searchParams.set('view', 'pv-products');
    copyPreview(new URL(location.href), fallback);
    return fallback;
  }
  function currentReturn(id) {
    const detail = document.querySelector('[data-pv-product-page]');
    if (detail) {
      const incoming = new URL(location.href).searchParams.getAll('pvfrom');
      return incoming.length === 1 ? safeReturn(incoming[0], detail.dataset.productId) : null;
    }
    const source = new URL(location.href);
    source.hash = `pv-card-${id}`;
    // Shopify can render the catalog as a safe fallback for an unavailable alternate template.
    if (document.querySelector('[data-series-root]') && CATALOG_PATH.test(source.pathname) && !CATALOG_VIEWS.has(source.searchParams.get('view') || 'pv-products')) source.searchParams.set('view', 'pv-products');
    return safeReturn(source.href, id);
  }
  function detailURL(link) {
    const id = link.dataset.productId;
    if (!PRODUCT_ID.test(id || '')) return null;
    // Remove an earlier pvfrom before applying the URL-size bound to the fixed native destination.
    const input = localURL(link.getAttribute('href'), MAX_RETURN_LENGTH * 3 + 1024);
    if (!input) return null;
    input.searchParams.delete('pvfrom');
    const url = localURL(input.href);
    if (!url || !CATALOG_PATH.test(url.pathname) || url.searchParams.getAll('view').length !== 1 || url.searchParams.get('view') !== `pv-p-${id}`) return null;
    const output = new URL(url.pathname, location.origin);
    output.searchParams.set('view', `pv-p-${id}`);
    copyPreview(new URL(location.href), output);
    return output;
  }
  function prepareLink(link) {
    const target = detailURL(link), from = currentReturn(link.dataset.productId);
    if (!target) return null;
    if (from) target.searchParams.set('pvfrom', relative(from));
    link.setAttribute('href', relative(target));
    return from;
  }
  function updateLinks() {
    document.querySelectorAll('a[data-pv-product-link]').forEach(prepareLink);
    document.querySelectorAll('[data-pv-product-page]').forEach(root => {
      if (root.dataset.productTitle) document.title = `${root.dataset.productTitle} | PETSVILLE`;
      const incoming = new URL(location.href).searchParams.getAll('pvfrom');
      const from = incoming.length === 1 ? safeReturn(incoming[0], root.dataset.productId) : null;
      const destination = from || fallbackCatalog(root);
      root.querySelectorAll('a[data-pv-product-back]').forEach(link => {
        link.setAttribute('href', relative(destination));
        link.textContent = from ? HOME_PATH.test(from.pathname) ? '← Back to homepage' : '← Return to results' : '← All Products';
      });
      root.querySelectorAll('a[data-pv-product-collection]').forEach(link => {
        const url = localURL(root.dataset.collectionUrl || link.getAttribute('href'));
        if (!url || !CATALOG_PATH.test(url.pathname) || !CATALOG_VIEWS.has(url.searchParams.get('view'))) return;
        copyPreview(new URL(location.href), url);
        link.setAttribute('href', relative(url));
      });
    });
  }
  function migrateLegacy() {
    if (!document.querySelector('[data-series-root]') || !CATALOG_PATH.test(location.pathname)) return false;
    let hash; try { hash = decodeURIComponent(location.hash); } catch (_) { return false; }
    const legacy = hash.match(/^#pv-(?:product|title)-([a-z0-9][a-z0-9-]{0,95})$/);
    if (!legacy) return false;
    const card = document.getElementById(`pv-card-${legacy[1]}`);
    if (!card || !card.matches('[data-series-product]') || card.dataset.productId !== legacy[1]) return false;
    const link = card.querySelector('a[data-pv-product-link]');
    if (!link || !detailURL(link) || !prepareLink(link)) return false;
    if (legacyDestination !== link.href) { legacyDestination = link.href; location.replace(link.href); }
    return true;
  }
  function restoreCard() {
    if (!restorePending) return;
    restorePending = false;
    let hash; try { hash = decodeURIComponent(location.hash); } catch (_) { return; }
    const match = hash.match(/^#pv-card-([a-z0-9][a-z0-9-]{0,95})$/);
    if (!match) return;
    const card = document.getElementById(`pv-card-${match[1]}`);
    if (!card || (card.dataset.productId && card.dataset.productId !== match[1])) return;
    if (card.hidden && window.PVSeriesCatalog) window.PVSeriesCatalog.revealCard(match[1]);
    if (card.hidden) return;
    const links = [...card.querySelectorAll('a[data-pv-product-link]')].filter(item => item.dataset.productId === match[1]);
    const link = links.find(item => item.matches('[data-series-image-open]') || item.querySelector('img, picture')) || links[0];
    if (!link) return;
    link.focus({ preventScroll: true });
    if (card.scrollIntoView) card.scrollIntoView({ block: 'center', behavior: 'auto' });
  }
  function scheduleRestore() {
    restorePending = true;
    const version = ++restoreVersion;
    // The second frame follows native history/BFCache scroll restoration.
    frame(() => frame(() => { if (version === restoreVersion) restoreCard(); }));
  }
  function init() { updateLinks(); if (!migrateLegacy()) scheduleRestore(); }
  window.PVProductNavigation = { init };
  window.addEventListener('click', event => {
    if (!window.Shopify || !window.Shopify.designMode || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[data-pv-product-link]');
    if (!link || link.hasAttribute('download') || (link.target && link.target.toLowerCase() !== '_self') || !detailURL(link)) return;
    const from = prepareLink(link);
    // Shopify's editor classifies these unassigned product templates as external links. Navigate this same-origin preview frame directly.
    event.preventDefault();
    event.stopPropagation();
    if (from && !document.querySelector('[data-pv-product-page]')) {
      const source = new URL(location.href);
      source.hash = from.hash;
      history.replaceState(history.state, '', source);
    }
    location.assign(link.href);
  }, true);
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[data-pv-product-link]');
    if (!link || link.hasAttribute('download') || (link.target && link.target.toLowerCase() !== '_self')) return;
    const from = prepareLink(link);
    if (!from || document.querySelector('[data-pv-product-page]')) return;
    const source = new URL(location.href);
    source.hash = from.hash;
    // Replace only the source entry. The browser performs the ordinary document navigation.
    history.replaceState(history.state, '', source);
  });
  document.addEventListener('pv:catalog-rendered', () => { updateLinks(); if (restorePending) scheduleRestore(); });
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', init);
  window.addEventListener('popstate', init);
  window.addEventListener('hashchange', init);
  init();
})();
